import { FinancialBrokerDealer, IAccount, IEmailProviderConfiguration, UserSessionContext } from '@ViewModels';
import { AdminUserSessionContext } from '../viewmodels/AdminViewModels';

const ADMIN_ENTRYPOINT = 'admin.html';

function getCurrentOrigin() {
	return typeof window === 'undefined' ? '' : window.location.origin;
}

export function buildAdminDeepLink(path: string, origin = getCurrentOrigin()) {
	const normalizedPath = path.startsWith('/') ? path : `/${path}`;
	return `${origin}/${ADMIN_ENTRYPOINT}#${normalizedPath}`;
}

export function buildAdminAccountUrl(accountId: string, origin?: string) {
	return buildAdminDeepLink(`/accounts/${encodeURIComponent(accountId)}`, origin);
}

// `view=desk` opens the ticket conversation. Without it the support page renders the ticket table
// and ignores `ticketId`, so links pasted into GitHub issues or new tabs land nowhere useful.
export function buildAdminSupportTicketUrl(ticketId: string, origin?: string) {
	const params = new URLSearchParams({ view: 'desk', ticketId });
	return buildAdminDeepLink(`/support?${params.toString()}`, origin);
}

export function booleanToString(field: boolean) {
	if (field) {
		return field ? 'Yes' : 'No';
	}
	return 'No';
}

export function numberToString(field: number) {
	return field !== undefined && field !== null ? field.toString() : 'Not available';
}

export function formatDate(d: Date) {
	return d ? new Date(Date.parse(d.toString())).toLocaleDateString() : 'Not available';
}

export const UserRoleName: Record<string, string> = {
	admin: 'Administrator',
	limitedUser: 'Limited User',
	superAdmin: 'Super Administrator',
	user: 'User',
};

// Compared case-insensitively to match the API, which treats the stored broker dealer as OrdinalIgnoreCase.
export const isNorthwesternMutualBrokerDealer = (account?: IAccount | null): boolean =>
	account?.additionalInfo?.brokerDealer?.toLowerCase() === FinancialBrokerDealer.NorthwesternMutual.toLowerCase();

export function areEmailProvidersConfigured(account: IAccount) {
	if (!account || !account.emailProviderConfigurations) {
		return false;
	}

	for (const configuration of account.emailProviderConfigurations) {
		if (!isEmailProviderConfigured(configuration)) {
			return false;
		}
	}

	return true;
}

export function isEmailProviderConfigured(providerConfiguration: IEmailProviderConfiguration) {
	if (!providerConfiguration.emailProvider) {
		return false;
	}

	switch (providerConfiguration.emailProvider) {
		case 'Yahoo':
		case 'Aol':
		case 'Imap':
			return (
				!!providerConfiguration.imap &&
				!!providerConfiguration.imap.imapHost &&
				!!providerConfiguration.imap.imapPort &&
				!!providerConfiguration.imap.smtpHost &&
				!!providerConfiguration.imap.smtpPort
			);
		case 'Exchange':
			return !!providerConfiguration.exchange && !!providerConfiguration.exchange.webServicesUrl;
		default:
			return true;
	}
}

/** @returns If the user session is an admin user session, a value will be returned. Null otherwise. */
export const asAdminUserSession = <TUserSession extends UserSessionContext = UserSessionContext>(
	userSession: TUserSession
) => {
	if (userSession instanceof AdminUserSessionContext) {
		return userSession;
	}
	return null;
};
